import "./style.css";

import modelManifest from "./generated/model-manifest";
import type { WorkerResponse } from "./types";
import type { WorkerRequest } from "./worker/messages";


const worker = new Worker(new URL("./worker/inference-worker.ts", import.meta.url), {
  type: "module",
});

const app = document.querySelector<HTMLDivElement>("#app");
if (!app) {
  throw new Error("Missing #app root.");
}

app.innerHTML = `
  <div class="splash-screen" aria-hidden="true">
    <div class="splash-panel">
      <span class="splash-note">♪</span>
      <span class="splash-title">TRANSKUN</span>
    </div>
  </div>

  <main class="app-shell">
    <section class="intro">
      <div class="app-mark" aria-hidden="true">♪</div>
      <h1>トランスクン</h1>
      <p class="lead">
        ピアノ音源から MIDI をつくろう
      </p>
    </section>

    <section class="converter-card">
      <div class="converter-main">
        <label class="dropzone" for="audio-file">
          <span class="dropzone-kicker">音声・動画ファイル</span>
          <strong id="file-name">クリックして音声・動画を選択</strong>
          <span>WAV / MP3 / M4A / MP4 / WebM などに対応</span>
          <input id="audio-file" class="sr-only" type="file" accept="audio/*,video/*,.wav,.mp3,.m4a,.aac,.flac,.ogg,.opus,.mp4,.m4v,.mov,.webm,.ogv" />
        </label>

        <div class="control-stack">
          <section class="swing-control" aria-label="採譜エンジン">
            <div class="control-heading">
              <div>
                <span class="section-label">採譜エンジン</span>
                <p id="engine-help">まずは音声・動画を選択してください。</p>
              </div>
              <strong id="note-summary">--</strong>
            </div>
            <div class="engine-meter" aria-hidden="true">
              <span></span>
              <span></span>
              <span></span>
              <span></span>
              <span></span>
            </div>
            <div class="range-labels">
              <span>解析</span>
              <span>MIDI</span>
            </div>
          </section>

          <details class="advanced-settings">
            <summary>
              <span>詳細情報</span>
              <span class="details-toggle-mark" aria-hidden="true"></span>
            </summary>
            <div class="advanced-grid">
              <label class="field">
                <span>ランタイム</span>
                <output id="runtime">準備中</output>
              </label>
              <label class="field">
                <span>実行プロバイダー</span>
                <output id="providers">-</output>
              </label>
              <label class="field">
                <span>検出ノート数</span>
                <output id="note-count">-</output>
              </label>
            </div>
          </details>
        </div>
      </div>

      <div class="action-row">
        <button id="start-button" class="btn-lg-primary main-action" type="button" disabled>変換する</button>
        <button id="cancel-button" class="btn-lg-secondary" type="button" hidden disabled>中止</button>
      </div>

      <div class="status-area">
        <div id="progress-meter" class="progress-meter" role="progressbar" aria-label="変換の進み具合" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0" aria-valuetext="0%" data-progress="0%" hidden></div>
        <p id="status-copy">準備しています。</p>
      </div>

      <section id="result-panel" class="result-panel" hidden>
        <p>変換が完了しました。MIDI ファイルを保存してください。</p>
        <a id="download-link" class="btn-lg-primary download-link" download="transkun-output.mid" hidden>MIDI をダウンロード</a>
      </section>

      <section class="trust-row" aria-label="このアプリの特徴">
        <span>※すべての処理はお使いの端末内で完結します。選択したファイルがサーバーに送信されることはありません。</span>
      </section>

      <section class="sr-only" aria-hidden="true">
        <strong id="worker-state">Booting</strong>
        <strong id="stage-state">Idle</strong>
        <div id="event-log">Waiting for worker initialization...</div>
      </section>
    </section>
  </main>
`;

const splashScreen = document.querySelector<HTMLElement>(".splash-screen");
if (splashScreen) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      window.setTimeout(() => {
        splashScreen.classList.add("is-hidden");
      }, 2200);
      window.setTimeout(() => {
        splashScreen.remove();
      }, 3000);
    });
  });
}

function requireElement<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (!element) {
    throw new Error(`Missing required DOM node: ${selector}`);
  }
  return element;
}

const statusCopy = requireElement<HTMLElement>("#status-copy");
const runtime = requireElement<HTMLElement>("#runtime");
const providers = requireElement<HTMLElement>("#providers");
const noteCount = requireElement<HTMLElement>("#note-count");
const noteSummary = requireElement<HTMLElement>("#note-summary");
const engineHelp = requireElement<HTMLElement>("#engine-help");
const log = requireElement<HTMLElement>("#event-log");
const fileInput = requireElement<HTMLInputElement>("#audio-file");
const fileName = requireElement<HTMLElement>("#file-name");
const startButton = requireElement<HTMLButtonElement>("#start-button");
const cancelButton = requireElement<HTMLButtonElement>("#cancel-button");
const downloadLink = requireElement<HTMLAnchorElement>("#download-link");
const workerState = requireElement<HTMLElement>("#worker-state");
const stageState = requireElement<HTMLElement>("#stage-state");
const progressMeter = requireElement<HTMLElement>("#progress-meter");
const resultPanel = requireElement<HTMLElement>("#result-panel");

let ready = false;
let running = false;
let downloadUrl: string | null = null;
let displayedProgress = 0;

function appendLog(line: string): void {
  const prefix = new Date().toLocaleTimeString("ja-JP", { hour12: false });
  log.textContent = `[${prefix}] ${line}\n${log.textContent}`;
}

function send(message: WorkerRequest): void {
  worker.postMessage(message);
}

function setStatus(message: string): void {
  statusCopy.textContent = message;
}

function setProgress(value: number, options: { reset?: boolean } = {}): void {
  const targetPercent = Math.max(0, Math.min(100, value));
  const percent = options.reset ? targetPercent : Math.max(displayedProgress, targetPercent);
  const roundedPercent = Math.round(percent);
  displayedProgress = percent;
  progressMeter.style.setProperty("--progress", `${percent}%`);
  progressMeter.dataset.progress = `${roundedPercent}%`;
  progressMeter.setAttribute("aria-valuenow", String(roundedPercent));
  progressMeter.setAttribute("aria-valuetext", `${roundedPercent}%`);
}

function setProgressVisible(visible: boolean): void {
  progressMeter.hidden = !visible;
}

function clearDownload(): void {
  if (downloadUrl) {
    URL.revokeObjectURL(downloadUrl);
    downloadUrl = null;
  }
  downloadLink.removeAttribute("href");
  downloadLink.hidden = true;
  resultPanel.hidden = true;
}

function setRunning(nextRunning: boolean): void {
  running = nextRunning;
  const hasFile = Boolean(fileInput.files?.[0]);
  startButton.disabled = !ready || running || !hasFile;
  startButton.textContent = running ? "変換中..." : "変換する";
  cancelButton.hidden = !running;
  cancelButton.disabled = !running;
  fileInput.disabled = running;
}

function updateSelectedFileLabel(): void {
  const file = fileInput.files?.[0];
  fileName.textContent = file ? file.name : "クリックして音声・動画を選択";
  if (!file) {
    engineHelp.textContent = ready ? "音声・動画を選択してください。" : "変換エンジンを準備しています。";
    return;
  }

  engineHelp.textContent = ready ? "変換できます。" : "ファイルを選択しました。準備完了までお待ちください。";
}

function segmentIndexFromDetail(detail: string): number | null {
  const match = detail.match(/segment\s+(\d+)/i);
  if (!match) {
    return null;
  }
  const index = Number(match[1]);
  return Number.isFinite(index) ? index : null;
}

function progressFromWorkerStage(stage: string, detail: string): number {
  if (stage === "boot") {
    return detail.includes("Starting") ? 8 : 4;
  }
  if (stage === "decode") {
    if (detail.includes("ffmpeg")) {
      return 18;
    }
    if (detail.includes("Preparing")) {
      return 28;
    }
    return 14;
  }
  if (stage === "features") {
    const index = segmentIndexFromDetail(detail) ?? 1;
    return Math.min(70, 30 + (index * 4));
  }
  if (stage === "inference") {
    const index = segmentIndexFromDetail(detail) ?? 1;
    return Math.min(84, 38 + (index * 4));
  }
  if (stage === "postprocess") {
    return detail.includes("Encoding") ? 96 : 90;
  }
  if (stage === "cancel") {
    return displayedProgress;
  }
  return 20;
}

function messageFromWorkerStage(stage: string): string {
  if (stage === "boot") {
    return "採譜エンジンを準備しています。";
  }
  if (stage === "decode") {
    return "音声トラックを読み込んでいます。";
  }
  if (stage === "features") {
    return "音の特徴を解析しています。";
  }
  if (stage === "inference") {
    return "ピアノノートを推定しています。";
  }
  if (stage === "postprocess") {
    return "MIDI ファイルを書き出しています。";
  }
  if (stage === "cancel") {
    return "中止しています。";
  }
  return "処理しています。";
}

worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
  const data = event.data;

  if (data.type === "progress") {
    stageState.textContent = data.stage;
    appendLog(`${data.stage}: ${data.detail}`);
    setStatus(messageFromWorkerStage(data.stage));
    setProgressVisible(true);
    setProgress(progressFromWorkerStage(data.stage, data.detail));
    return;
  }

  if (data.type === "ready") {
    ready = true;
    workerState.textContent = "Ready";
    runtime.textContent = data.runtime;
    providers.textContent = data.providers.join(", ");
    appendLog(`Worker ready (${data.runtime}).`);
    updateSelectedFileLabel();
    setStatus(fileInput.files?.[0] ? "ファイルを選択しました。変換できます。" : "まずは音声・動画を選んでください。");
    setProgress(0, { reset: true });
    setProgressVisible(false);
    setRunning(false);
    return;
  }

  if (data.type === "result") {
    setRunning(false);
    setProgressVisible(true);
    setProgress(100);
    noteCount.textContent = `${data.noteCount}`;
    noteSummary.textContent = `${data.noteCount}`;
    engineHelp.textContent = "検出ノート数";
    clearDownload();
    downloadUrl = URL.createObjectURL(new Blob([data.midi], { type: "audio/midi" }));
    downloadLink.href = downloadUrl;
    downloadLink.hidden = false;
    resultPanel.hidden = false;
    setStatus("変換が完了しました。MIDI ファイルを保存してください。");
    appendLog(`Completed with ${data.noteCount} notes.`);
    return;
  }

  if (data.type === "error") {
    setRunning(false);
    if (data.detail === "Processing cancelled.") {
      setStatus("変換を中止しました。");
      appendLog("Cancelled.");
      setProgressVisible(false);
      return;
    }

    setStatus(`エラーが発生しました。${data.detail}`);
    appendLog(`Error: ${data.detail}`);
  }
};

fileInput.addEventListener("change", () => {
  clearDownload();
  noteCount.textContent = "-";
  noteSummary.textContent = "--";
  setProgress(0, { reset: true });
  setProgressVisible(false);
  updateSelectedFileLabel();
  if (fileInput.files?.[0] && ready) {
    setStatus("ファイルを選択しました。変換できます。");
  }
  setRunning(false);
});

startButton.addEventListener("click", () => {
  const file = fileInput.files?.[0];
  if (!file) {
    setStatus("先に音声・動画ファイルを選択してください。");
    appendLog("No media file selected.");
    return;
  }

  clearDownload();
  noteCount.textContent = "-";
  noteSummary.textContent = "--";
  engineHelp.textContent = "解析中";
  setProgress(0, { reset: true });
  setProgressVisible(true);
  setRunning(true);
  setStatus("採譜を開始しています。");
  appendLog(`Request transcription: ${file.name}.`);
  send({ type: "transcribe", file });
});

cancelButton.addEventListener("click", () => {
  cancelButton.disabled = true;
  setStatus("中止を要求しました。");
  send({ type: "cancel" });
});

send({
  type: "init",
  manifest: modelManifest,
});
